import * as THREE from 'three';

const materials = new Map<string, THREE.MeshStandardMaterial>();
export function material(color: string, roughness = 0.8, metalness = 0): THREE.MeshStandardMaterial {
  const key = `${color}:${roughness}:${metalness}`;
  if (!materials.has(key)) materials.set(key, new THREE.MeshStandardMaterial({ color, roughness, metalness, flatShading: false }));
  return materials.get(key)!;
}
export function mesh(geometry: THREE.BufferGeometry, color: string | THREE.Material, parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Mesh {
  const object = new THREE.Mesh(geometry, typeof color === 'string' ? material(color) : color);
  object.position.set(x, y, z); object.castShadow = true; object.receiveShadow = true; parent.add(object); return object;
}
export function box(parent: THREE.Object3D, x: number, y: number, z: number, w: number, h: number, d: number, color: string | THREE.Material): THREE.Mesh {
  return mesh(new THREE.BoxGeometry(w, h, d), color, parent, x, y, z);
}
export function ball(parent: THREE.Object3D, x: number, y: number, z: number, sx: number, sy: number, sz: number, color: string): THREE.Mesh {
  const m = mesh(new THREE.SphereGeometry(1, 16, 10), color, parent, x, y, z); m.scale.set(sx, sy, sz); return m;
}
export function tube(parent: THREE.Object3D, points: THREE.Vector3[], radius: number, color: string): THREE.Mesh {
  return mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 14, radius, 5, false), color, parent);
}
export function random(seed: number): () => number {
  let value = seed >>> 0; return () => { value = Math.imul(1664525, value) + 1013904223 | 0; return (value >>> 0) / 4294967296; };
}
export function texture(kind: 'stone' | 'cloth' | 'soil'): THREE.CanvasTexture {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = 256;
  const c = canvas.getContext('2d')!; const rand = random(22);
  c.fillStyle = kind === 'stone' ? '#b4b6a0' : kind === 'cloth' ? '#f7f2df' : '#535c36'; c.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 6000; i++) { c.fillStyle = `rgba(${rand() > .5 ? '255,255,234' : '33,48,36'},${rand() * .14})`; c.fillRect(rand() * 256, rand() * 256, 1 + rand() * 2, 1 + rand() * 2); }
  if (kind === 'stone') { c.strokeStyle = '#868e7c'; c.lineWidth = 3; c.strokeRect(1, 1, 254, 254); c.strokeStyle = '#d6d4bb'; c.lineWidth = 1; c.strokeRect(4, 4, 248, 248); }
  if (kind === 'cloth') {
    c.fillStyle = '#b4454540';
    for (let i = 0; i < 256; i += 64) { c.fillRect(i, 0, 28, 256); c.fillRect(0, i, 256, 28); }
    c.strokeStyle = '#fff9'; c.lineWidth = .6;
    for (let i = 0; i < 256; i += 4) { c.beginPath(); c.moveTo(i, 0); c.lineTo(i, 256); c.moveTo(0, i); c.lineTo(256, i); c.stroke(); }
  }
  const tex = new THREE.CanvasTexture(canvas); tex.colorSpace = THREE.SRGBColorSpace; tex.wrapS = tex.wrapT = THREE.RepeatWrapping; return tex;
}

export function leaf(parent: THREE.Object3D, x: number, y: number, z: number, length: number, width: number, color: string, angle = 0): THREE.Group {
  const group = new THREE.Group(); parent.add(group); group.position.set(x, y, z); group.rotation.y = angle;
  const shape = new THREE.Shape(); shape.moveTo(0, 0); shape.bezierCurveTo(-width, length * .35, -width * .5, length * .85, 0, length); shape.bezierCurveTo(width * .5, length * .85, width, length * .35, 0, 0);
  const blade = mesh(new THREE.ShapeGeometry(shape, 8), new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide, roughness: .75 }), group); blade.rotation.x = -Math.PI / 2;
  tube(group, [new THREE.Vector3(0, .03, 0), new THREE.Vector3(0, .06, -length)], .025, '#b6c174');
  for (let i = 1; i < 5; i++) for (const sign of [-1, 1]) tube(group, [new THREE.Vector3(0, .04, -length * i / 6), new THREE.Vector3(sign * width * .55, .05, -length * (i + .65) / 6)], .012, '#a6b669');
  return group;
}

export function makeAnt(): { group: THREE.Group; animate: (time: number, moving: boolean) => void } {
  const group = new THREE.Group();
  ball(group, 0, .28, .28, .19, .18, .26, '#61362b');
  ball(group, 0, .29, -.03, .12, .13, .17, '#7a4631');
  ball(group, 0, .31, -.28, .2, .18, .18, '#895133');
  for (const sign of [-1, 1]) {
    ball(group, sign * .125, .4, -.39, .065, .065, .045, '#fff8df');
    ball(group, sign * .125, .4, -.427, .03, .035, .018, '#211d18');
    tube(group, [new THREE.Vector3(sign * .1, .45, -.3), new THREE.Vector3(sign * .22, .67, -.42), new THREE.Vector3(sign * .29, .65, -.55)], .017, '#573629');
  }
  const legs: THREE.Group[] = [];
  for (let i = 0; i < 3; i++) for (const sign of [-1, 1]) {
    const leg = new THREE.Group(); group.add(leg); leg.position.z = -.2 + i * .18;
    tube(leg, [new THREE.Vector3(sign * .07, .27, 0), new THREE.Vector3(sign * .29, .24, (i - 1) * .08), new THREE.Vector3(sign * .4, .03, (i - 1) * .17)], .022, '#523027'); legs.push(leg);
  }
  // A small leaf satchel gives the silhouette a recognizable focal point.
  ball(group, 0, .43, .18, .14, .12, .16, '#749253');
  tube(group, [new THREE.Vector3(-.12, .38, .04), new THREE.Vector3(0, .48, .06), new THREE.Vector3(.12, .38, .04)], .017, '#c6bc79');
  return { group, animate: (time, moving) => { legs.forEach((l, i) => { l.rotation.y = moving ? Math.sin(time * 24 + i * Math.PI) * .28 : 0; }); group.position.y = moving ? Math.sin(time * 48) * .014 : 0; } };
}

export function croissant(parent: THREE.Object3D, x: number, z: number): void {
  const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = -.4; parent.add(g);
  for (let i = 0; i < 11; i++) {
    const a = -.95 + i / 10 * 1.9; const size = .55 + Math.sin(i / 10 * Math.PI) * 1.5;
    const m = ball(g, Math.sin(a) * 5.3, 1.25, Math.cos(a) * 3.2, size * .68, size * .72, size, i % 2 ? '#d68c39' : '#edb45f'); m.rotation.y = a;
    const slash = mesh(new THREE.TorusGeometry(size * .7, .065, 4, 12, Math.PI), '#f6cc82', g, Math.sin(a) * 5.3, 1.25, Math.cos(a) * 3.2); slash.rotation.y = a;
  }
}

export function cup(parent: THREE.Object3D, x: number, z: number): void {
  const g = new THREE.Group(); g.position.set(x, 0, z); parent.add(g);
  mesh(new THREE.CylinderGeometry(5.2, 4.8, .32, 48), '#e9e8d4', g, 0, .2);
  const profile = [new THREE.Vector2(0, .4), new THREE.Vector2(2, .4), new THREE.Vector2(2.5, .7), new THREE.Vector2(3, 4.9), new THREE.Vector2(2.78, 5), new THREE.Vector2(2.3, .8)];
  mesh(new THREE.LatheGeometry(profile, 40), material('#e9efdf', .32), g);
  mesh(new THREE.CylinderGeometry(2.78, 2.78, .05, 40), '#543528', g, 0, 4.25);
  mesh(new THREE.TorusGeometry(1.5, .32, 10, 24), '#e1e7d3', g, 4.1, 2.7);
  const band = mesh(new THREE.TorusGeometry(2.87, .09, 8, 48), '#528a7c', g, 0, 4.25); band.rotation.x = Math.PI / 2;
}

export function planter(parent: THREE.Object3D, x: number, z: number, scale = 1): THREE.Group {
  const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(scale); parent.add(g);
  mesh(new THREE.CylinderGeometry(5.6, 4.1, 8, 24), '#b57959', g, 0, 4);
  mesh(new THREE.CylinderGeometry(6, 6, .9, 24), '#ca906b', g, 0, 7.7);
  mesh(new THREE.CylinderGeometry(5.55, 5.55, .1, 24), '#504934', g, 0, 8.2);
  const r = random(62);
  for (let i = 0; i < 13; i++) {
    const angle = r() * Math.PI * 2; const h = 4 + r() * 6; const dx = Math.cos(angle) * 3; const dz = Math.sin(angle) * 3;
    tube(g, [new THREE.Vector3(0, 8, 0), new THREE.Vector3(dx * .6, 8 + h * .6, dz * .6), new THREE.Vector3(dx, 8 + h, dz)], .07, '#577341');
    const l = leaf(g, dx, 8 + h, dz, 4 + r() * 2, 1.5, i % 2 ? '#548355' : '#789c56', angle); l.rotation.z = .25;
  }
  return g;
}
